import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import { useTranslation } from 'react-i18next';
import { getAvatarImage, shouldMirrorAvatar } from './avatars/avatarManager';
import EmergencyProtocolModal from './EmergencyProtocolModal';
import CustomDropdown from './CustomDropdown';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import StrategyPreferenceRewardModal from './StrategyPreferenceRewardModal';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial';

// Main factions only with their colors
const mainFactions = [
  'Remnants of Humanity',
  'Asrah',
  'Exolot Dominion',
  'The Sentients',
  'Ra\'s Children',
  'League of Shadows'
];

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Asrah': '#8B0000',
  'Exolot Dominion': '#4B0082',
  'The Sentients': '#ADD8E6',
  'Ra\'s Children': '#F4A460',
  'League of Shadows': '#1A1A1A'
};

function Profile({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Play profile music
  useMusic('/music/rock_4.mp3');
  const [factionStats, setFactionStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Avatar state
  const [avatars, setAvatars] = useState([]);
  const [selectedGeneral, setSelectedGeneral] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState('');
  const [openSelector, setOpenSelector] = useState(null); // 'general', 'advisor', or null
  const [unlockedAvatars, setUnlockedAvatars] = useState(new Set(['human_1', 'human_2'])); // Always include human_1 and human_2
  
  // Preparation bonus hover state
  const [hoveredBonus, setHoveredBonus] = useState(null); // 'plan_ahead', 'black_market', 'team_leader', or null
  
  // Preparation bonus selected state
  const [selectedBonus, setSelectedBonus] = useState(null); // 'plan_ahead', 'black_market', 'team_leader', or null
  
  // Diplomatic accords hover state
  const [hoveredAccord, setHoveredAccord] = useState(null); // 'strategic_coordination', 'public_relations', 'emergency_protocol', 'command_directive', or null
  
  // Diplomatic accords selected state
  const [selectedAccord, setSelectedAccord] = useState(null); // 'strategic_coordination', 'public_relations', 'emergency_protocol', 'command_directive', or null
  
  // Available features from database
  const [availableFeatures, setAvailableFeatures] = useState([]);
  
  // Emergency Protocol modal state
  const [showEmergencyProtocolModal, setShowEmergencyProtocolModal] = useState(false);
  const [emergencyProtocolMetadata, setEmergencyProtocolMetadata] = useState(null);

  // Section navigation state
  const [currentSection, setCurrentSection] = useState(0); // 0 = Available Orders, 1 = Consumables, 2 = Emotes, 3 = Titles, 4 = Season Stats
  const sections = [
    t('profile.availableOrders'),
    t('profile.consumables.title'),
    t('profile.emotes.title'),
    t('profile.titles.title'),
    'Season Stats'
  ];

  // Consumables state
  const [orderDuplicators, setOrderDuplicators] = useState({
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0
  });
  const [billyBucks, setBillyBucks] = useState(0);
  const [consumablesLoading, setConsumablesLoading] = useState(true);

  // Emotes state
  const [emotes, setEmotes] = useState([]);
  const [emotesLoading, setEmotesLoading] = useState(true);
  const [selectedEmotes, setSelectedEmotes] = useState({}); // { emoteType: unlockKey } e.g., { "Greet": "emote_greet_variant1" }

  // Titles state
  const [titles, setTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [titlesLoading, setTitlesLoading] = useState(true);

  // Season Stats state
  const [seasonStats, setSeasonStats] = useState({
    botBattles: { wins: 0, losses: 0 },
    pvpBattles: { wins: 0, losses: 0 },
    topStrategies: []
  });
  const [seasonStatsLoading, setSeasonStatsLoading] = useState(true);

  // Avatar purchase dialog state
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseAvatar, setPurchaseAvatar] = useState(null);
  const [purchaseCost, setPurchaseCost] = useState(0);
  const [purchaseUnlockKey, setPurchaseUnlockKey] = useState('');

  // Strategy preference selection state
  const [hasSelectedStrategyPreference, setHasSelectedStrategyPreference] = useState(false);
  const [showStrategyPreferenceModal, setShowStrategyPreferenceModal] = useState(false);
  const [showStrategyRewardModal, setShowStrategyRewardModal] = useState(false);
  const [grantedOrders, setGrantedOrders] = useState([]);
  const [selectingPreference, setSelectingPreference] = useState(false);

  // Rename user state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');

  // Tutorial state
  const [showTutorialProfile, setShowTutorialProfile] = useState(false);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !loading && !seenTutorialMessages.includes('profile')) {
      const timer = setTimeout(() => setShowTutorialProfile(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, seenTutorialMessages]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const res = await fetch(`${API_URL}/profile/stats?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Accept-Language': normalizedLanguage,
          },
        });
        
        if (!res.ok) {
          console.error('Failed to fetch profile stats:', res.status);
          return;
        }
        
        const data = await res.json();
        // Filter to only show main factions
        const mainFactionStats = data.filter(stat => mainFactions.includes(stat.faction));
        setFactionStats(mainFactionStats);
      } catch (err) {
        console.error('Error fetching profile stats:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/get-avatar`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setAvatars(data);
        } else {
          console.error('Unexpected avatar response format:', data);
          setAvatars([]);
        }
      } catch (err) {
        console.error('Failed to fetch avatars:', err);
        setAvatars([]);
      }
    };

    const fetchUnlockedAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/unlocked-avatars`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          setUnlockedAvatars(new Set(data.unlockedAvatars || ['human_1', 'human_2']));
        } else {
          // Default to only human_1 and human_2 if API fails
          setUnlockedAvatars(new Set(['human_1', 'human_2']));
        }
      } catch (err) {
        console.error('Failed to fetch unlocked avatars:', err);
        // Default to only human_1 and human_2 on error
        setUnlockedAvatars(new Set(['human_1', 'human_2']));
      }
    };

    const fetchAvatarSelections = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/avatars`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          const general = data.general || localStorage.getItem('selectedGeneral') || '';
          const advisor = data.advisor || localStorage.getItem('selectedAdvisor') || '';
          setSelectedGeneral(general);
          setSelectedAdvisor(advisor);
          // Sync to localStorage as fallback
          if (general) localStorage.setItem('selectedGeneral', general);
          if (advisor) localStorage.setItem('selectedAdvisor', advisor);
        } else {
          // Fallback to localStorage if API fails
          const general = localStorage.getItem('selectedGeneral') || '';
          const advisor = localStorage.getItem('selectedAdvisor') || '';
          setSelectedGeneral(general);
          setSelectedAdvisor(advisor);
        }
      } catch (err) {
        console.error('Failed to fetch avatar selections:', err);
        // Fallback to localStorage
        const general = localStorage.getItem('selectedGeneral') || '';
        const advisor = localStorage.getItem('selectedAdvisor') || '';
        setSelectedGeneral(general);
        setSelectedAdvisor(advisor);
      }
    };

    const fetchProfileFeatures = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/features`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          setAvailableFeatures(data.availableFeatures || []);
          
          // Set current selections
          if (data.selections) {
            setSelectedBonus(data.selections.preparation_bonus || null);
            setSelectedAccord(data.selections.diplomatic_accords || null);
          }
          
          // Set emergency protocol metadata if available
          if (data.metadata) {
            // Parse metadata if it's a string (from JSONB)
            const metadata = typeof data.metadata === 'string' 
              ? JSON.parse(data.metadata) 
              : data.metadata;
            setEmergencyProtocolMetadata(metadata);
          } else {
            setEmergencyProtocolMetadata(null);
          }
        } else {
          console.error('Failed to fetch profile features:', res.status);
          setAvailableFeatures([]);
        }
      } catch (err) {
        console.error('Error fetching profile features:', err);
        setAvailableFeatures([]);
      }
    };

    fetchStats();
    fetchAvatars();
    fetchAvatarSelections();
    fetchProfileFeatures();
    fetchConsumables();
    fetchUnlockedAvatars();
    fetchEmotes();
    fetchTitles();
    fetchStrategyPreferenceStatus();
    fetchSeasonStats();
  }, [i18nInstance.language]);

  const fetchStrategyPreferenceStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/profile/strategy-preference-status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasSelectedStrategyPreference(data.hasSelected || false);
      } else {
        console.error('Failed to fetch strategy preference status:', res.status);
      }
    } catch (err) {
      console.error('Error fetching strategy preference status:', err);
    }
  };

  const handleSelectStrategyPreference = async (preference) => {
    if (selectingPreference) return;
    
    setSelectingPreference(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to select a strategy preference. Please log in again.');
        return;
      }

      console.log('Selecting strategy preference:', preference);
      console.log('API_URL:', API_URL);
      console.log('Token exists:', !!token);

      const res = await fetch(`${API_URL}/profile/select-strategy-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preference }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasSelectedStrategyPreference(true);
        setShowStrategyPreferenceModal(false);
        setGrantedOrders(data.grantedOrders || []);
        setShowStrategyRewardModal(true);
        playSoundEffect('/sounds/click.mp3');
      } else {
        const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}: ${res.statusText}` }));
        console.error('Strategy preference selection failed:', errorData);
        alert(errorData.error || `Failed to select strategy preference (${res.status})`);
      }
    } catch (err) {
      console.error('Error selecting strategy preference:', err);
      alert(`Failed to select strategy preference: ${err.message || 'Network error'}`);
    } finally {
      setSelectingPreference(false);
    }
  };

  const fetchConsumables = async () => {
    try {
      setConsumablesLoading(true);
      const res = await fetch(`${API_URL}/profile/consumables`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrderDuplicators(data.duplicators || {
          common: 0,
          rare: 0,
          epic: 0,
          legendary: 0
        });
        setBillyBucks(data.billyBucks || 0);
      } else {
        console.error('Failed to fetch consumables:', res.status);
        setOrderDuplicators({ common: 0, rare: 0, epic: 0, legendary: 0 });
        setBillyBucks(0);
      }
    } catch (err) {
      console.error('Error fetching consumables:', err);
      setOrderDuplicators({ common: 0, rare: 0, epic: 0, legendary: 0 });
      setBillyBucks(0);
    } finally {
      setConsumablesLoading(false);
    }
  };

  const fetchEmotes = async () => {
    try {
      setEmotesLoading(true);
      const res = await fetch(`${API_URL}/profile/emotes`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmotes(data.emotes || []);
        // Load saved emote selections
        if (data.selections) {
          setSelectedEmotes(data.selections);
        }
      } else {
        console.error('Failed to fetch emotes:', res.status);
        setEmotes([]);
      }
    } catch (err) {
      console.error('Error fetching emotes:', err);
      setEmotes([]);
    } finally {
      setEmotesLoading(false);
    }
  };

  const fetchTitles = async () => {
    try {
      setTitlesLoading(true);
      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      
      const res = await fetch(`${API_URL}/profile/titles?lang=${encodeURIComponent(normalizedLanguage)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Accept-Language': normalizedLanguage,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setTitles(data.titles || []);
        setSelectedTitle(data.selectedTitle || null);
      } else {
        console.error('Failed to fetch titles:', res.status);
        setTitles([]);
        setSelectedTitle(null);
      }
    } catch (err) {
      console.error('Error fetching titles:', err);
      setTitles([]);
      setSelectedTitle(null);
    } finally {
      setTitlesLoading(false);
    }
  };

  const fetchSeasonStats = async () => {
    try {
      setSeasonStatsLoading(true);
      const res = await fetch(`${API_URL}/profile/season-stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setSeasonStats({
          botBattles: data.botBattles || { wins: 0, losses: 0 },
          pvpBattles: data.pvpBattles || { wins: 0, losses: 0 },
          topStrategies: data.topStrategies || []
        });
      } else {
        console.error('Failed to fetch season stats:', res.status);
        setSeasonStats({
          botBattles: { wins: 0, losses: 0 },
          pvpBattles: { wins: 0, losses: 0 },
          topStrategies: []
        });
      }
    } catch (err) {
      console.error('Error fetching season stats:', err);
      setSeasonStats({
        botBattles: { wins: 0, losses: 0 },
        pvpBattles: { wins: 0, losses: 0 },
        topStrategies: []
      });
    } finally {
      setSeasonStatsLoading(false);
    }
  };

  const handleTitleSelect = async (titleId) => {
    // Update local state immediately
    const selectedTitleData = titles.find(t => t.id === titleId);
    setSelectedTitle(selectedTitleData ? { id: titleId, name: selectedTitleData.name } : null);
    
    // Update titles array to reflect selection
    setTitles(prevTitles => 
      prevTitles.map(title => ({
        ...title,
        isSelected: title.id === titleId
      }))
    );
    
    // Save to database
    try {
      const res = await fetch(`${API_URL}/profile/titles/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ titleId }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Failed to save title selection:', errorData.error || res.statusText);
        // Revert on error - refetch titles
        fetchTitles();
      } else {
        console.log('✅ Title selection saved:', { titleId });
      }
    } catch (err) {
      console.error('Error saving title selection:', err);
      // Revert on error - refetch titles
      fetchTitles();
    }
  };

  const handleEmoteSelect = async (emoteType, unlockKey) => {
    // Update local state immediately
    setSelectedEmotes(prev => ({
      ...prev,
      [emoteType]: unlockKey
    }));
    
    // Save to database
    try {
      const res = await fetch(`${API_URL}/profile/emotes/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          emoteType,
          unlockKey: unlockKey || ''
        }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Failed to save emote selection:', errorData.error || res.statusText);
        // Revert on error
        setSelectedEmotes(prev => {
          const updated = { ...prev };
          delete updated[emoteType];
          return updated;
        });
      } else {
        console.log('✅ Emote selection saved:', { emoteType, unlockKey });
      }
    } catch (err) {
      console.error('Error saving emote selection:', err);
      // Revert on error
      setSelectedEmotes(prev => {
        const updated = { ...prev };
        delete updated[emoteType];
        return updated;
      });
    }
  };

  // Helper function to translate emote type names
  const translateEmoteType = (emoteType) => {
    const emoteTypeMap = {
      'Greet': 'profile.emotes.greet',
      'Taunt': 'profile.emotes.taunt',
      'Laugh': 'profile.emotes.laugh',
      'Good Game': 'profile.emotes.goodGame',
      'Wish Luck': 'profile.emotes.wishLuck',
      'Wake Up': 'profile.emotes.wakeUp',
      'Thanks': 'profile.emotes.thanks'
    };
    return emoteTypeMap[emoteType] ? t(emoteTypeMap[emoteType]) : emoteType;
  };

  // Group emotes by type (name)
  const emotesByType = React.useMemo(() => {
    const grouped = {};
    emotes.forEach(emote => {
      const type = emote.name; // "Greet", "Taunt", etc.
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(emote);
    });
    // Sort types by the first emote's sort order
    return Object.keys(grouped)
      .sort((a, b) => {
        const aOrder = grouped[a][0]?.sortOrder || 0;
        const bOrder = grouped[b][0]?.sortOrder || 0;
        return aOrder - bOrder;
      })
      .reduce((acc, key) => {
        acc[key] = grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        return acc;
      }, {});
  }, [emotes]);

  const getFactionColor = (factionName) => {
    return factionColors[factionName] || '#808080'; // Default gray if faction not found
  };

  const isAvatarUnlocked = (avatarName) => {
    // human_1 and human_2 are always unlocked
    if (avatarName === 'human_1' || avatarName === 'human_2') {
      return true;
    }
    return unlockedAvatars.has(avatarName);
  };

  const handleAvatarSelect = async (avatarName, type, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Special handling for billy_2 - show purchase dialog if not unlocked
    if (avatarName === 'billy_2' && !isAvatarUnlocked(avatarName)) {
      console.log('billy_2 clicked, fetching purchase info...');
      try {
        // Fetch purchase info
        const res = await fetch(`${API_URL}/profile/avatars/purchase-info/${avatarName}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('Purchase info received:', data);
          setPurchaseAvatar({ name: avatarName, type });
          setPurchaseCost(data.cost || 1000000);
          setPurchaseUnlockKey(data.unlockKey || 'billy_2');
          setShowPurchaseDialog(true);
          console.log('Purchase dialog should now be visible');
        } else {
          const errorText = await res.text();
          console.error('Failed to fetch purchase info for billy_2:', res.status, errorText);
          // Fallback: show dialog with default cost
          setPurchaseAvatar({ name: avatarName, type });
          setPurchaseCost(1000000);
          setPurchaseUnlockKey('billy_2');
          setShowPurchaseDialog(true);
        }
      } catch (err) {
        console.error('Error fetching purchase info:', err);
        // Fallback: show dialog with default cost
        setPurchaseAvatar({ name: avatarName, type });
        setPurchaseCost(1000000);
        setPurchaseUnlockKey('billy_2');
        setShowPurchaseDialog(true);
      }
      return;
    }
    
    // Prevent selection of locked avatars
    if (!isAvatarUnlocked(avatarName)) {
      return;
    }
    
    let newGeneral = selectedGeneral;
    let newAdvisor = selectedAdvisor;
    
    if (type === 'general') {
      newGeneral = avatarName;
      // If advisor is the same, clear it
      if (selectedAdvisor === avatarName) {
        newAdvisor = '';
      }
    } else {
      newAdvisor = avatarName;
    }
    
    // Update state immediately
    setSelectedGeneral(newGeneral);
    setSelectedAdvisor(newAdvisor);
    
    // Update localStorage as fallback
    localStorage.setItem('selectedGeneral', newGeneral);
    localStorage.setItem('selectedAdvisor', newAdvisor);
    
    // Save to database
    try {
      const res = await fetch(`${API_URL}/profile/avatars`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          general: newGeneral,
          advisor: newAdvisor
        }),
      });
      
      if (!res.ok) {
        console.error('Failed to save avatar selections to database');
      }
    } catch (err) {
      console.error('Error saving avatar selections:', err);
    }
    
    setOpenSelector(null);
  };

  const handlePurchaseAvatar = async () => {
    if (!purchaseAvatar || !purchaseUnlockKey) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/profile/avatars/grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          unlockKey: purchaseUnlockKey
        }),
      });
      
      if (res.ok) {
        // Refresh unlocked avatars and billy bucks
        await fetchUnlockedAvatars();
        await fetchConsumables();
        
        // Close dialog
        setShowPurchaseDialog(false);
        setPurchaseAvatar(null);
        setPurchaseCost(0);
        setPurchaseUnlockKey('');
        
        // Automatically select the avatar if purchase was successful
        if (purchaseAvatar) {
          handleAvatarSelect(purchaseAvatar.name, purchaseAvatar.type, null);
        }
      } else {
        const errorData = await res.json();
        alert(errorData.error || 'Failed to purchase avatar');
      }
    } catch (err) {
      console.error('Error purchasing avatar:', err);
      alert('Failed to purchase avatar');
    }
  };

  const handleRenameUser = async () => {
    if (!newUsername || newUsername.trim().length < 3) {
      setRenameError('Username must be at least 3 characters long');
      return;
    }

    if (newUsername.trim().length > 50) {
      setRenameError('Username must be no more than 50 characters long');
      return;
    }

    setRenaming(true);
    setRenameError('');

    try {
      const res = await fetch(`${API_URL}/profile/rename`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          username: newUsername.trim()
        }),
      });

      const data = await res.json();

      if (res.ok) {
        // Update user object if it's an object
        if (typeof user === 'object' && user !== null) {
          user.username = data.username;
        }
        
        // Update the displayed username in the top left
        // The user prop might need to be updated from parent, but we can at least update local state
        
        playSoundEffect('/sounds/click.mp3');
        setShowRenameModal(false);
        setNewUsername('');
        setRenameError('');
        
        // Show success message
        alert(`Username successfully changed to "${data.username}"`);
        
        // Reload the page to reflect the change everywhere
        window.location.reload();
      } else {
        setRenameError(data.error || 'Failed to rename user');
      }
    } catch (err) {
      console.error('Error renaming user:', err);
      setRenameError('Failed to rename user. Please try again.');
    } finally {
      setRenaming(false);
    }
  };

  const getAvailableAvatars = (type) => {
    if (type === 'general') {
      return avatars;
    } else {
      // Advisor can't be the same as general
      return avatars.filter(avatar => avatar.avatar_name !== selectedGeneral);
    }
  };

  const hasFeature = (featureKey) => {
    return availableFeatures.includes(featureKey);
  };

  const handleFeatureSelect = async (category, featureKey) => {
    // Special handling for emergency_protocol - open modal instead of toggling
    if (featureKey === 'emergency_protocol') {
      // If already selected, open modal to edit
      // If not selected, select it first, then open modal
      if (selectedAccord !== 'emergency_protocol') {
        // Update local state immediately to show selection
        setSelectedAccord('emergency_protocol');
        // Then save to database
        await saveFeatureSelection(category, featureKey, null);
      }
      setShowEmergencyProtocolModal(true);
      return;
    }
    
    // Store previous values for potential rollback
    const previousBonus = selectedBonus;
    const previousAccord = selectedAccord;
    
    // Toggle selection: if already selected, deselect (set to null)
    const newSelection = (category === 'preparation_bonus' && selectedBonus === featureKey) ||
                         (category === 'diplomatic_accords' && selectedAccord === featureKey)
                         ? null : featureKey;
    
    // Update local state immediately
    if (category === 'preparation_bonus') {
      setSelectedBonus(newSelection);
    } else {
      setSelectedAccord(newSelection);
    }
    
    // Save to database
    await saveFeatureSelection(category, newSelection, null, previousBonus, previousAccord);
  };

  const saveFeatureSelection = async (category, selectedOption, metadata, previousBonus = null, previousAccord = null) => {
    try {
      const res = await fetch(`${API_URL}/profile/features`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          category,
          selectedOption,
          metadata
        }),
      });
      
      const responseData = await res.json();
      
      if (!res.ok) {
        console.error('Failed to save profile feature selection:', responseData.error || res.statusText);
        // Revert on error
        if (category === 'preparation_bonus' && previousBonus !== null) {
          setSelectedBonus(previousBonus);
        } else if (category === 'diplomatic_accords' && previousAccord !== null) {
          setSelectedAccord(previousAccord);
        }
      } else {
        // Success - verify the response matches what we sent
        console.log('✅ Profile selection saved:', responseData);
        // Update metadata if returned
        if (responseData.metadata && category === 'diplomatic_accords') {
          // Parse metadata if it's a string (from JSONB)
          const metadata = typeof responseData.metadata === 'string' 
            ? JSON.parse(responseData.metadata) 
            : responseData.metadata;
          setEmergencyProtocolMetadata(metadata);
        } else if (category === 'diplomatic_accords' && !selectedOption) {
          // Clear metadata if deselecting
          setEmergencyProtocolMetadata(null);
        }
      }
    } catch (err) {
      console.error('Error saving profile feature selection:', err);
      // Revert on error
      if (category === 'preparation_bonus' && previousBonus !== null) {
        setSelectedBonus(previousBonus);
      } else if (category === 'diplomatic_accords' && previousAccord !== null) {
        setSelectedAccord(previousAccord);
      }
    }
  };

  const handleEmergencyProtocolSave = async (metadata) => {
    await saveFeatureSelection('diplomatic_accords', 'emergency_protocol', metadata);
    setShowEmergencyProtocolModal(false);
  };

  // Define all possible features for each category
  const preparationBonusFeatures = [
    { key: 'plan_ahead', image: '/images/plan_ahead.jpg', alt: 'Plan Ahead' },
    { key: 'black_market', image: '/images/black_market_dossier.jpg', alt: 'Black Market Dossier' },
    { key: 'team_leader', image: '/images/team_leader_briefing.jpg', alt: 'Team Leader Briefing' }
  ];

  const diplomaticAccordsFeatures = [
    { key: 'strategic_coordination', image: '/images/strategic_coordination.jpg', alt: 'Strategic Coordination' },
    { key: 'public_relations', image: '/images/public_relations.jpg', alt: 'Public Relations' },
    { key: 'emergency_protocol', image: '/images/emergency_protocol.jpg', alt: 'Emergency Protocol' },
    { key: 'command_directive', image: '/images/command_directive.jpg', alt: 'Command Directive' }
  ];

  return (
    <div className="profile-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="profile-fullscreen-background"
        style={{ backgroundImage: 'url(/images/profile.jpg)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`profile-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="profile-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

      {/* Rename User Button - Below Player Toolbar */}
      <div style={{ 
        position: 'absolute',
        top: '50px',
        left: '10px',
        zIndex: 1000
      }}>
        <button
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setNewUsername(typeof user === 'string' ? user : (user?.username || ''));
            setRenameError('');
            setShowRenameModal(true);
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#45a049';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#4CAF50';
          }}
        >
          Rename User
        </button>
      </div>

      <button onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        navigate('/welcome');
      }} className="back-button">
        {t('common.back')}
      </button>

      {/* Strategy Preference Selection Button - Top Middle */}
      {!hasSelectedStrategyPreference && (
        <button
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setShowStrategyPreferenceModal(true);
          }}
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '14px 28px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#000',
            border: '2px solid #FFD700',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            zIndex: 1000,
            boxShadow: '0 4px 15px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3)',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.7), 0 0 30px rgba(255, 215, 0, 0.5)';
            e.target.style.transform = 'translateX(-50%) scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3)';
            e.target.style.transform = 'translateX(-50%) scale(1)';
          }}
        >
          Tell us who you are
        </button>
      )}

      {/* Avatar Selection - Upper Middle */}
      <div className="avatar-selection-container">
        <div className="avatar-box-wrapper">
          <div 
            className={`avatar-box ${openSelector === 'general' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setOpenSelector(openSelector === 'general' ? null : 'general');
            }}
          >
            <label className="avatar-label">{t('battleOverview.general')}</label>
            <img 
              src={getAvatarImage(selectedGeneral)} 
              alt={selectedGeneral || 'General'} 
              className="avatar-preview"
              style={{
                transform: shouldMirrorAvatar(selectedGeneral, 'left', avatars) ? 'scaleX(-1)' : 'none'
              }}
            />
          </div>
          {openSelector === 'general' && (
            <div className="avatar-selector" onClick={(e) => e.stopPropagation()}>
              {getAvailableAvatars('general').map((avatar) => {
                const isUnlocked = isAvatarUnlocked(avatar.avatar_name);
                const isBilly2 = avatar.avatar_name === 'billy_2';
                // billy_2 should be clickable even when locked (to show purchase dialog)
                const isClickable = isUnlocked || isBilly2;
                const isLockedButClickable = isBilly2 && !isUnlocked;
                return (
                  <div
                    key={avatar.id}
                    className={`avatar-option ${selectedGeneral === avatar.avatar_name ? 'selected' : ''} ${!isUnlocked && !isBilly2 ? 'disabled' : ''} ${isLockedButClickable ? 'locked-clickable' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isClickable) {
                        playSoundEffect('/sounds/click.mp3');
                      }
                      handleAvatarSelect(avatar.avatar_name, 'general', e);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <img 
                      src={getAvatarImage(avatar.avatar_name)} 
                      alt={avatar.avatar_name}
                      className="avatar-option-image"
                      style={{
                        transform: shouldMirrorAvatar(avatar.avatar_name, 'left', avatars) ? 'scaleX(-1)' : 'none'
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="avatar-box-wrapper">
          <div 
            className={`avatar-box ${openSelector === 'advisor' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setOpenSelector(openSelector === 'advisor' ? null : 'advisor');
            }}
          >
            <label className="avatar-label">{t('battleOverview.advisor')}</label>
            <img 
              src={getAvatarImage(selectedAdvisor)} 
              alt={selectedAdvisor || 'Advisor'} 
              className="avatar-preview"
              style={{
                transform: shouldMirrorAvatar(selectedAdvisor, 'right', avatars) ? 'scaleX(-1)' : 'none'
              }}
            />
          </div>
          {openSelector === 'advisor' && (
            <div className="avatar-selector" onClick={(e) => e.stopPropagation()}>
              {getAvailableAvatars('advisor').map((avatar) => {
                const isUnlocked = isAvatarUnlocked(avatar.avatar_name);
                const isBilly2 = avatar.avatar_name === 'billy_2';
                // billy_2 should be clickable even when locked (to show purchase dialog)
                const isClickable = isUnlocked || isBilly2;
                const isLockedButClickable = isBilly2 && !isUnlocked;
                return (
                  <div
                    key={avatar.id}
                    className={`avatar-option ${selectedAdvisor === avatar.avatar_name ? 'selected' : ''} ${!isUnlocked && !isBilly2 ? 'disabled' : ''} ${isLockedButClickable ? 'locked-clickable' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isClickable) {
                        playSoundEffect('/sounds/click.mp3');
                      }
                      handleAvatarSelect(avatar.avatar_name, 'advisor', e);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <img 
                      src={getAvatarImage(avatar.avatar_name)} 
                      alt={avatar.avatar_name}
                      className="avatar-option-image"
                      style={{
                        transform: shouldMirrorAvatar(avatar.avatar_name, 'right', avatars) ? 'scaleX(-1)' : 'none'
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Preparation Bonus and Diplomatic Accords Sections - Middle Lower Half */}
      <div className="bonus-sections-container">
        {/* Preparation Bonus Section */}
        <div className="preparation-bonus-container">
          <h2 className="preparation-bonus-title">{t('profile.preparationBonus.title')}</h2>
          <p className="preparation-bonus-description">
            {t('profile.preparationBonus.description')}
          </p>
          <div className="preparation-bonus-boxes">
            {preparationBonusFeatures.map((feature) => {
              const isAvailable = hasFeature(feature.key);
              const isSelected = selectedBonus === feature.key;
              return (
                <div 
                  key={feature.key}
                  className={`preparation-bonus-box ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isAvailable) {
                      playSoundEffect('/sounds/click.mp3');
                      handleFeatureSelect('preparation_bonus', feature.key);
                    }
                  }}
                  onMouseEnter={() => {
                    if (isAvailable) {
                      setHoveredBonus(feature.key);
                    }
                  }}
                  onMouseLeave={() => setHoveredBonus(null)}
                >
                  <img 
                    src={feature.image} 
                    alt={feature.alt} 
                    className="preparation-bonus-image"
                  />
                </div>
              );
            })}
          </div>
          <div className={`preparation-bonus-tooltip ${hoveredBonus ? 'visible' : ''}`}>
            {hoveredBonus === 'plan_ahead' && (
              <p>{t('profile.preparationBonus.planAhead')}</p>
            )}
            {hoveredBonus === 'black_market' && (
              <p>{t('profile.preparationBonus.blackMarketDossier')}</p>
            )}
            {hoveredBonus === 'team_leader' && (
              <p>{t('profile.preparationBonus.teamLeaderMeeting')}</p>
            )}
            {!hoveredBonus && <p>&nbsp;</p>}
          </div>
        </div>

        {/* Diplomatic Accords Section */}
        <div className="preparation-bonus-container diplomatic-accords-container">
          <h2 className="preparation-bonus-title">{t('profile.diplomaticAccords.title')}</h2>
          <p className="preparation-bonus-description">
            {t('profile.diplomaticAccords.description')}
          </p>
          <div className="preparation-bonus-boxes">
            {diplomaticAccordsFeatures.map((feature) => {
              const isAvailable = hasFeature(feature.key);
              const isSelected = selectedAccord === feature.key;
              return (
                <div 
                  key={feature.key}
                  className={`preparation-bonus-box ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isAvailable) {
                      playSoundEffect('/sounds/click.mp3');
                      handleFeatureSelect('diplomatic_accords', feature.key);
                    }
                  }}
                  onMouseEnter={() => {
                    if (isAvailable) {
                      setHoveredAccord(feature.key);
                    }
                  }}
                  onMouseLeave={() => setHoveredAccord(null)}
                >
                  <img 
                    src={feature.image} 
                    alt={feature.alt} 
                    className="preparation-bonus-image"
                  />
                </div>
              );
            })}
          </div>
          <div className={`preparation-bonus-tooltip ${hoveredAccord ? 'visible' : ''}`}>
            {hoveredAccord === 'strategic_coordination' && (
              <p>{t('profile.diplomaticAccords.strategicCoordination')}</p>
            )}
            {hoveredAccord === 'public_relations' && (
              <p>{t('profile.diplomaticAccords.publicRelations')}</p>
            )}
            {hoveredAccord === 'emergency_protocol' && (
              <p>{t('profile.diplomaticAccords.emergencyProtocol')}</p>
            )}
            {hoveredAccord === 'command_directive' && (
              <p>{t('profile.diplomaticAccords.commandDirective')}</p>
            )}
            {!hoveredAccord && <p>&nbsp;</p>}
          </div>
        </div>
      </div>

      {/* Section Navigation at the bottom */}
      <div className="faction-stats-container">
        {/* Section Navigation Arrows */}
        <div className="section-navigation">
          <button 
            className="section-arrow section-arrow-left"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setCurrentSection((prev) => (prev === 0 ? sections.length - 1 : prev - 1));
            }}
            aria-label="Previous section"
          >
            ←
          </button>
          <h2 className="faction-stats-title">
            {currentSection === 0 ? t('profile.availableOrders.title') : 
             currentSection === 1 ? t('profile.consumables.title') : 
             currentSection === 2 ? t('profile.emotes.title') : 
             currentSection === 3 ? t('profile.titles.title') :
             'Season Stats'}
          </h2>
          <button 
            className="section-arrow section-arrow-right"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setCurrentSection((prev) => (prev === sections.length - 1 ? 0 : prev + 1));
            }}
            aria-label="Next section"
          >
            →
          </button>
        </div>

        {/* Available Orders Section */}
        {currentSection === 0 && (
          <>
            {loading ? (
              <div className="faction-stats-loading">Loading statistics...</div>
            ) : factionStats.length === 0 ? (
              <div className="faction-stats-loading">No statistics available</div>
            ) : (
              <div className="faction-stats-list">
                {factionStats.map((stat) => {
                  const factionColor = getFactionColor(stat.faction);
                  const displayName = stat.faction_translated || stat.faction;
                  return (
                    <div key={stat.faction} className="faction-stat-item">
                      <div className="faction-stat-row">
                        <span className="faction-stat-name">{displayName}</span>
                        <div className="faction-stat-bar-container">
                          <div 
                            className="faction-stat-bar"
                            style={{
                              width: `${stat.percentage}%`,
                              backgroundColor: factionColor
                            }}
                          />
                          <span className="faction-stat-percentage-text">{stat.percentage}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Consumables Section */}
        {currentSection === 1 && (
          <>
            {consumablesLoading ? (
              <div className="faction-stats-loading">Loading consumables...</div>
            ) : (
              <div className="consumables-list">
                {/* Billy Bucks */}
                <div className="consumable-item">
                  <div className="consumable-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <img 
                        src="/images/billy_bucks.png" 
                        alt="Billy Bucks" 
                        style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                      />
                      <span className="consumable-name">Billy Bucks</span>
                    </div>
                    <span className="consumable-quantity">{billyBucks}</span>
                  </div>
                </div>

                {/* Order Duplicators */}
                <div className="consumable-item">
                  <div className="consumable-row">
                    <span className="consumable-name" style={{ color: '#9d9d9d' }}>{t('profile.consumables.commonDuplicator')}</span>
                    <span className="consumable-quantity">{orderDuplicators.common}</span>
                  </div>
                </div>
                <div className="consumable-item">
                  <div className="consumable-row">
                    <span className="consumable-name" style={{ color: '#0070dd' }}>{t('profile.consumables.rareDuplicator')}</span>
                    <span className="consumable-quantity">{orderDuplicators.rare}</span>
                  </div>
                </div>
                <div className="consumable-item">
                  <div className="consumable-row">
                    <span className="consumable-name" style={{ color: '#a335ee' }}>{t('profile.consumables.epicDuplicator')}</span>
                    <span className="consumable-quantity">{orderDuplicators.epic}</span>
                  </div>
                </div>
                <div className="consumable-item">
                  <div className="consumable-row">
                    <span className="consumable-name" style={{ color: '#ff8000' }}>{t('profile.consumables.legendaryDuplicator')}</span>
                    <span className="consumable-quantity">{orderDuplicators.legendary}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Emotes Section */}
        {currentSection === 2 && (
          <>
            {emotesLoading ? (
              <div className="faction-stats-loading">Loading emotes...</div>
            ) : (
              <div className="emotes-list">
                {/* List all emote types with picklists */}
                {Object.keys(emotesByType).map(emoteType => {
                  const emotesOfType = emotesByType[emoteType];
                  
                  // Filter out locked emotes - only show unlocked ones
                  const unlockedEmotes = emotesOfType.filter(emote => emote.isUnlocked);
                  
                  // Convert emotes to dropdown options format
                  const translatedEmoteType = translateEmoteType(emoteType);
                  const dropdownOptions = [
                    { value: '', label: `-- ${t('profile.emotes.select')} ${translatedEmoteType} --` },
                    ...unlockedEmotes.map(emote => ({
                      value: emote.unlockKey,
                      label: `"${emote.speechBubble}"`
                    }))
                  ];
                  
                  return (
                    <div key={emoteType} className="emote-type-row">
                      <span className="emote-type-name">{translatedEmoteType}</span>
                      <div className="emote-dropdown-wrapper">
                        <CustomDropdown
                          options={dropdownOptions}
                          value={selectedEmotes[emoteType] || ''}
                          onChange={(value) => {
                            playSoundEffect('/sounds/click.mp3');
                            handleEmoteSelect(emoteType, value);
                          }}
                          placeholder={`-- ${t('profile.emotes.select')} ${translatedEmoteType} --`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Titles Section */}
        {currentSection === 3 && (
          <>
            {titlesLoading ? (
              <div className="faction-stats-loading">{t('profile.titles.loading')}</div>
            ) : (
              <div className="titles-container">
                <div className="titles-current-selection">
                  {selectedTitle ? (
                    <div className="titles-selected-title">
                      <span className="titles-selected-label">{t('profile.titles.current')}:</span>
                      <span className="titles-selected-name">{selectedTitle.name}</span>
                    </div>
                  ) : (
                    <div className="titles-no-selection">
                      {t('profile.titles.noSelection')}
                    </div>
                  )}
                </div>
                <div className="titles-list">
                  {titles.map(title => (
                    <div
                      key={title.id}
                      className={`title-item ${title.isSelected ? 'title-item-selected' : ''}`}
                      onClick={() => {
                        playSoundEffect('/sounds/click.mp3');
                        handleTitleSelect(title.id);
                      }}
                    >
                      <span className="title-name">{title.name}</span>
                      {title.isSelected && (
                        <span className="title-selected-indicator">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Season Stats Section */}
        {currentSection === 4 && (
          <>
            {seasonStatsLoading ? (
              <div className="faction-stats-loading">Loading season stats...</div>
            ) : (
              <div className="season-stats-container">
                {/* Battle Stats - Combined into one compact section */}
                <div className="season-battles-section">
                  <div className="season-battle-row">
                    <span className="season-battle-label">Bot Battles</span>
                    <span className="season-battle-record">
                      <span className="season-stat-wins">{seasonStats.botBattles.wins}</span>W{' '}
                      <span className="season-stat-losses">{seasonStats.botBattles.losses}</span>L
                    </span>
                  </div>
                  <div className="season-battle-row">
                    <span className="season-battle-label">PVP Battles</span>
                    <span className="season-battle-record">
                      <span className="season-stat-wins">{seasonStats.pvpBattles.wins}</span>W{' '}
                      <span className="season-stat-losses">{seasonStats.pvpBattles.losses}</span>L
                    </span>
                  </div>
                </div>

                {/* Top 5 Strategies */}
                <div className="season-strategies-section">
                  <div className="season-strategies-header">Top 5 Strategies</div>
                  {seasonStats.topStrategies.length === 0 ? (
                    <div className="season-stat-empty">No strategies played yet</div>
                  ) : (
                    <div className="season-strategies-list">
                      {seasonStats.topStrategies.map((strategy, index) => (
                        <div key={strategy.id} className="season-strategy-item">
                          <span className="season-strategy-name">
                            {index + 1}. {strategy.name}
                          </span>
                          <span className="season-strategy-record">
                            <span className="season-stat-wins">{strategy.wins}</span>W{' '}
                            <span className="season-stat-losses">{strategy.losses}</span>L
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Emergency Protocol Modal */}
      <EmergencyProtocolModal
        isOpen={showEmergencyProtocolModal}
        onClose={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowEmergencyProtocolModal(false);
        }}
        onSave={handleEmergencyProtocolSave}
        initialData={emergencyProtocolMetadata}
      />

      {/* Strategy Preference Selection Modal */}
      {showStrategyPreferenceModal && (
        <div className="modal-overlay" onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowStrategyPreferenceModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
            <p style={{ color: '#fff', textAlign: 'center', marginBottom: '15px', fontSize: '16px' }}>
              {t('welcome.strategyPreference.description')}
            </p>
            <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px' }}>
              {t('welcome.strategyPreference.title')}
            </h2>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              alignItems: 'center'
            }}>
              {[
                { key: 'aggressive', value: 'Aggressive' },
                { key: 'greedy', value: 'Greedy' },
                { key: 'patient', value: 'Patient' },
                { key: 'controlling', value: 'Controlling' },
                { key: 'creative', value: 'Creative' }
              ].map((preference) => (
                <button
                  key={preference.value}
                  onClick={() => handleSelectStrategyPreference(preference.value)}
                  disabled={selectingPreference}
                  style={{
                    width: '80%',
                    padding: '15px 30px',
                    backgroundColor: selectingPreference ? '#666' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectingPreference ? 'not-allowed' : 'pointer',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectingPreference) {
                      e.target.style.backgroundColor = '#45a049';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectingPreference) {
                      e.target.style.backgroundColor = '#4CAF50';
                    }
                  }}
                >
                  {t(`welcome.strategyPreference.${preference.key}`)}
                </button>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setShowStrategyPreferenceModal(false);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Preference Reward Modal */}
      <StrategyPreferenceRewardModal
        isOpen={showStrategyRewardModal}
        onClose={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowStrategyRewardModal(false);
        }}
        grantedOrders={grantedOrders}
      />

      {/* Rename User Modal */}
      {showRenameModal && (
        <div className="modal-overlay" onClick={() => {
          if (!renaming) {
            playSoundEffect('/sounds/click.mp3');
            setShowRenameModal(false);
            setRenameError('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px' }}>
              Rename User
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#fff', display: 'block', marginBottom: '8px' }}>
                New Username:
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  setRenameError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !renaming && newUsername.trim().length >= 3) {
                    handleRenameUser();
                  }
                }}
                disabled={renaming}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: renaming ? '#333' : '#fff',
                  color: renaming ? '#999' : '#000'
                }}
                autoFocus
              />
              {renameError && (
                <p style={{ color: '#ff6b6b', marginTop: '8px', fontSize: '14px' }}>
                  {renameError}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  if (!renaming) {
                    playSoundEffect('/sounds/click.mp3');
                    setShowRenameModal(false);
                    setRenameError('');
                  }
                }}
                disabled={renaming}
                style={{
                  padding: '10px 20px',
                  backgroundColor: renaming ? '#666' : '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: renaming ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameUser}
                disabled={renaming || newUsername.trim().length < 3}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (renaming || newUsername.trim().length < 3) ? '#999' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (renaming || newUsername.trim().length < 3) ? 'not-allowed' : 'pointer'
                }}
              >
                {renaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar Purchase Dialog */}
      {showPurchaseDialog && (() => {
        // Convert i18n language to locale format (en-us -> en-US, de-de -> de-DE, es-es -> es-ES)
        const locale = (i18nInstance.language || 'en-us').replace(/^([a-z]{2})-([a-z]{2})$/i, (_, p1, p2) => `${p1}-${p2.toUpperCase()}`);
        
        return (
          <div className="modal-overlay" onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setShowPurchaseDialog(false);
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
              <h2>{t('profile.avatarPurchase.title')}</h2>
              <p>{t('profile.avatarPurchase.message', { cost: purchaseCost.toLocaleString(locale) })}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                <button
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    setShowPurchaseDialog(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handlePurchaseAvatar();
                  }}
                  disabled={billyBucks < purchaseCost}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: billyBucks >= purchaseCost ? '#4CAF50' : '#999',
                    color: 'white',
                    border: 'none',
                    cursor: billyBucks >= purchaseCost ? 'pointer' : 'not-allowed',
                    borderRadius: '4px'
                  }}
                >
                  {t('profile.avatarPurchase.purchaseButton', { cost: purchaseCost.toLocaleString(locale) })}
                </button>
              </div>
              {billyBucks < purchaseCost && (
                <p style={{ color: '#ff6b6b', marginTop: '10px', textAlign: 'center' }}>
                  {t('profile.avatarPurchase.insufficientFunds', { 
                    have: billyBucks.toLocaleString(locale), 
                    need: purchaseCost.toLocaleString(locale) 
                  })}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {showTutorialProfile && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain_2.png"
          text={t('profile.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'profile' }),
              });
              setSeenTutorialMessages(prev => prev.includes('profile') ? prev : [...prev, 'profile']);
            }
            setShowTutorialProfile(false);
          }}
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default Profile;

